import { env } from "@/config/env";

/**
 * Feature flags for effectiveness-native features
 * All flags default to false for production safety
 */

import type * as React from "react";

export interface EffectivenessFeatureFlags {
  time_audit: boolean;
  mit_mode: boolean;
  contrib_required: boolean;
  abandonment_sweep: boolean;
  decision_cards: boolean;
  // PPM Integration flags
  ppm_integration: boolean;
  smartsheet_sync: boolean;
  googlesheets_sync: boolean;
  // Compliance Mode flags
  compliance_mode: boolean;
  stage_gates: boolean;
  audit_trail: boolean;
  immutable_log: boolean;
  // Assisted Care flags
  assisted_care: boolean;
}

export const DEFAULT_EFFECTIVENESS_FLAGS: EffectivenessFeatureFlags = Object
  .freeze({
    time_audit: false,
    mit_mode: false,
    contrib_required: false,
    abandonment_sweep: false,
    decision_cards: false,
    // PPM Integration flags (default OFF for safe shipping)
    ppm_integration: false,
    smartsheet_sync: false,
    googlesheets_sync: false,
    // Compliance Mode flags (default OFF for safe shipping)
    compliance_mode: false,
    stage_gates: false,
    audit_trail: false,
    immutable_log: false,
    // Assisted Care flags (default OFF for safe shipping)
    assisted_care: false,
  });

/**
 * Get feature flags from environment with safe defaults
 * Environment variables format: VITE_FEATURE_<FLAG_NAME>=true
 */
export function getEffectivenessFlags(): EffectivenessFeatureFlags {
  return {
    time_audit: env.VITE_FEATURE_TIME_AUDIT === "true",
    mit_mode: env.VITE_FEATURE_MIT_MODE === "true",
    contrib_required: env.VITE_FEATURE_CONTRIB_REQUIRED === "true",
    abandonment_sweep: env.VITE_FEATURE_ABANDONMENT_SWEEP === "true",
    decision_cards: env.VITE_FEATURE_DECISION_CARDS === "true",
    // PPM Integration flags
    ppm_integration: env.VITE_FEATURE_PPM_INTEGRATION === "true",
    smartsheet_sync: env.VITE_FEATURE_SMARTSHEET_SYNC === "true",
    googlesheets_sync: env.VITE_FEATURE_GOOGLESHEETS_SYNC === "true",
    // Compliance Mode flags
    compliance_mode: env.VITE_FEATURE_COMPLIANCE_MODE === "true",
    stage_gates: env.VITE_FEATURE_STAGE_GATES === "true",
    audit_trail: env.VITE_FEATURE_AUDIT_TRAIL === "true",
    immutable_log: env.VITE_FEATURE_IMMUTABLE_LOG === "true",
    // Assisted Care flags
    assisted_care: env.VITE_FEATURE_ASSISTED_CARE === "true",
  };
}

/**
 * Runtime feature flag overrides for development
 * Only available in non-production environments
 */
class FeatureFlagManager {
  private overrides: Partial<EffectivenessFeatureFlags> = {};
  private envFlags: EffectivenessFeatureFlags;

  constructor() {
    this.envFlags = getEffectivenessFlags();
  }

  /**
   * Get current feature flags with overrides applied
   */
  getFlags(): EffectivenessFeatureFlags {
    return {
      ...this.envFlags,
      ...this.overrides,
    };
  }

  /**
   * Set runtime override for development (non-production only)
   */
  setOverride(flag: keyof EffectivenessFeatureFlags, enabled: boolean): void {
    if (import.meta.env.MODE === "production") {
      console.warn("Feature flag overrides are disabled in production");
      return;
    }

    this.overrides[flag] = enabled;
    console.log(`Feature flag ${flag} set to ${enabled}`);
  }

  /**
   * Clear all runtime overrides
   */
  clearOverrides(): void {
    if (import.meta.env.MODE === "production") {
      return;
    }

    this.overrides = {};
    console.log("All feature flag overrides cleared");
  }

  /**
   * Get flag status with source information
   */
  getFlagStatus(flag: keyof EffectivenessFeatureFlags): {
    enabled: boolean;
    source: "environment" | "override" | "default";
  } {
    const flags = this.getFlags();
    const enabled = flags[flag];

    if (this.overrides.hasOwnProperty(flag)) {
      return { enabled, source: "override" };
    }

    if (this.envFlags[flag] !== DEFAULT_EFFECTIVENESS_FLAGS[flag]) {
      return { enabled, source: "environment" };
    }

    return { enabled, source: "default" };
  }
}

// Global feature flag manager instance
export const featureFlagManager = new FeatureFlagManager();

/**
 * Hook to get current feature flags
 */
export function useEffectivenessFlags(): EffectivenessFeatureFlags {
  return featureFlagManager.getFlags();
}

/**
 * Type guard to check if a specific feature is enabled
 */
export function isFeatureEnabled(
  flag: keyof EffectivenessFeatureFlags,
): boolean {
  return featureFlagManager.getFlags()[flag];
}

/**
 * HOC to conditionally render components based on feature flags
 */
export function withFeatureFlag<P extends object>(
  flag: keyof EffectivenessFeatureFlags,
  Component: React.ComponentType<P>,
  fallback: React.ComponentType<P> | null = null,
) {
  return function FeatureFlaggedComponent(props: P) {
    const enabled = isFeatureEnabled(flag);

    if (enabled) {
      // Component rendering would happen in a React context
      return { component: Component, props, enabled: true };
    }

    return fallback ? { component: fallback, props, enabled: false } : null;
  };
}

// Convenience helpers for PPM and Compliance features
export const isPPMEnabled = () => isFeatureEnabled("ppm_integration");
export const isSmartsheetEnabled = () =>
  isPPMEnabled() && isFeatureEnabled("smartsheet_sync");
export const isGoogleSheetsEnabled = () =>
  isPPMEnabled() && isFeatureEnabled("googlesheets_sync");
export const isComplianceEnabled = () => isFeatureEnabled("compliance_mode");
export const isStageGatesEnabled = () =>
  isComplianceEnabled() && isFeatureEnabled("stage_gates");
export const isAuditTrailEnabled = () => isFeatureEnabled("audit_trail");
export const isImmutableLogEnabled = () =>
  isComplianceEnabled() && isFeatureEnabled("immutable_log");
export const isAssistedCareEnabled = () => isFeatureEnabled("assisted_care");

// Assisted care helpers
export const ASSISTED_CARE_FALLBACK_ROUTE = "/dashboard";

/**
 * Filter out assisted care entries when the flag is disabled
 */
export function filterAssistedCareOptions<T extends { id?: string; value?: string }>(
  items: T[],
): T[] {
  if (isAssistedCareEnabled()) return items;
  return items.filter((item) => {
    const key = (item.id || item.value || "").toLowerCase();
    return key !== "assisted_care" && key !== "assisted-care";
  });
}

/**
 * Provide a safe route for assisted care based on the flag
 */
export function resolveAssistedCareRoute(
  assistedRoute: string,
  fallbackRoute: string = ASSISTED_CARE_FALLBACK_ROUTE,
) {
  return isAssistedCareEnabled() ? assistedRoute : fallbackRoute;
}
